/** @jsx jsx */
import { jsx } from 'theme-ui'
import Collection from '../collection'
import { Container } from '../helpers/react'
import classNames from 'classnames'

interface Entry {
  removeTopSpacing?: boolean
  removeBottomSpacing?: boolean
  [key: string]: any
}

interface GridStyles {
  row?: {
    justifyContent?: string
  }
  col?: {
    alignItems?: string
  }
  [key: string]: any
}

interface EntryOptions {
  styles?: GridStyles
  [key: string]: any
}

interface CollectionModuleWrapperProps {
  entries: Entry[]
  layout?: 'pills' | 'grid' | string
  background?: string
  gridHorizontalAlignment?: 'center' | undefined
  gridVerticalAligment?: 'top' | 'center' | 'bottom'
  useContainer?: boolean
  options?: EntryOptions
}

const removeSpacing = (props: CollectionModuleWrapperProps): CollectionModuleWrapperProps => ({
  ...props,
  entries: (props.entries || []).map((e) => ({
    ...e,
    removeTopSpacing: true,
    removeBottomSpacing: true,
  })),
})

const getAlignmentOptions = (
  alignmentValue: 'top' | 'center' | 'bottom' | undefined,
  alignmentType: 'gridHorizontalAlignment' | 'gridVerticalAligment',
  alignmentMap: Record<string, string>,
): EntryOptions => {
  const alignment = alignmentMap[String(alignmentValue)]

  return alignment
    ? {
        styles: {
          ...(alignmentType === 'gridVerticalAligment'
            ? { col: { alignItems: alignment } }
            : { row: { justifyContent: alignment } }),
        },
      }
    : {}
}

const CollectionModuleWrapper: React.FC<CollectionModuleWrapperProps> = (props) => {
  const layoutClass = classNames({
    pills: props.layout === 'pills',
    grid: props.layout === 'grid',
    proposition: props.layout === 'proposition',
    [`${props.background}-background`]:
      (props.layout === 'pills' || props.layout === 'grid' || props.layout === 'proposition') &&
      props.background,
  })

  const horizontalAlignmentMap = { center: 'center' }
  const verticalAlignmentMap = {
    top: 'flex-start',
    center: 'center',
    bottom: 'flex-end',
  }

  const useSpacing = !!props.gridHorizontalAlignment || !!props.gridVerticalAligment

  const options = props.gridHorizontalAlignment
    ? getAlignmentOptions(
        props.gridHorizontalAlignment,
        'gridHorizontalAlignment',
        horizontalAlignmentMap,
      )
    : props.gridVerticalAligment
    ? getAlignmentOptions(props.gridVerticalAligment, 'gridVerticalAligment', verticalAlignmentMap)
    : props.options || {}

  return (
    <Choose>
      <When condition={props.useContainer}>
        <Container className={layoutClass}>
          <Collection {...(useSpacing ? removeSpacing(props) : props)} options={options} />
        </Container>
      </When>
      <Otherwise>
        <Collection {...(useSpacing ? removeSpacing(props) : props)} options={options} />
      </Otherwise>
    </Choose>
  )
}

export default CollectionModuleWrapper
